import Button from 'ext-ui/components/Button/Button'
import React, { useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeeds/CardFeeds.style'
import CardFeedsTable from 'src/app/CardFeeds/components/CardFeedsTable'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  cardFeedDetailsUrl,
  createCardFeedUrl,
  homeUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { Userpilot } from 'userpilot'

const CardFeeds: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const user = useSelector(selectUserListModelFromState)

  // Defensive: sidebar gates the menu entry, but a direct URL must redirect
  // home when either flag is off. Wait for the feature query to settle so we
  // don't bounce away during the initial paint.
  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsCardFeedsTab)
    analytics.track('card feeds - viewed')
  }, [analytics, isAllowed])

  // The CardFeedsTable owns the card-feeds query (it drives sort/filter/search),
  // so these callbacks let it report load failures and an empty org back up here
  // — keeping a single query source instead of fetching the list twice. The
  // table only invokes onEmpty after a fresh fetch with no filters applied, so
  // stale cache and filtered no-matches don't bounce a user who has feeds.
  const handleFeedsLoadError = useCallback((): void => {
    analytics.track('card feeds', { action: 'load failed' })
  }, [analytics])

  const handleNoFeeds = useCallback((): void => {
    history.replace(createCardFeedUrl())
  }, [history])

  // Hold the gate with a full-page loader while the feature query resolves —
  // otherwise we flash a blank screen before either the page or the redirect.
  if (isFeaturesLoading) {
    return <LoadingBackgroundPage />
  }

  if (!isAllowed) {
    return null
  }

  const handleHelpGuideClick = (): void => {
    Userpilot.trigger('resource_center:jNfDAOvnub')
    analytics.track('card feeds', { action: 'help guide clicked' })
  }

  const handleAddCardFeedClick = (): void => {
    analytics.track('card feeds - add card feed')
    history.push(createCardFeedUrl())
  }

  const handleSeeEnrolledCardsClick = (feedId: string): void => {
    analytics.track('card feeds', { action: 'see enrolled cards clicked' })
    history.push(cardFeedDetailsUrl(feedId))
  }

  const actions = (
    <div css={style.actions}>
      <Button
        variant="secondary"
        onClick={handleHelpGuideClick}
        data-testid="card-feeds-help-guide"
        iconLeft={
          <SvgExternalLink
            viewBox="0 0 24 24"
            css={style.helpGuideIcon}
            data-testid="card-feeds-help-guide-icon"
          />
        }
      >
        {translate('Help Guide')}
      </Button>
      <Button
        variant="primary"
        onClick={handleAddCardFeedClick}
        data-testid="card-feeds-add"
      >
        {`+ ${translate('Add Card Feed')}`}
      </Button>
    </div>
  )

  return (
    <>
      <Helmet>
        <title>{translate('Card Feeds')}</title>
      </Helmet>
      <LayoutPage
        title={<span>{translate('Card Feeds')}</span>}
        subtitle={
          <span css={style.subtitle}>
            {translate(
              'Connect your bank to bring existing cards into Extend and manage spend in one place.',
            )}
          </span>
        }
        actions={actions}
      >
        <CardFeedsTable
          organizationId={user?.organizationId}
          onSeeCards={handleSeeEnrolledCardsClick}
          onEmpty={handleNoFeeds}
          onLoadError={handleFeedsLoadError}
        />
      </LayoutPage>
    </>
  )
}

export default CardFeeds
